import { tr } from 'i18n';
import { useEffect, useState, useCallback, useRef } from 'react';
import moment from 'moment';
import {
  getLoadByStatus,
  getLoadHistory,
  getLoadStatistics,
} from 'service/LoadService';
import { getBlockedDrivers } from 'service/TabelService';
import { getLoadsCompleted, getLoadsInLoading } from 'service/ReportsService';
import { getTabCounts } from 'service/DriverRegistrationService';
import { canReadLoadQueues, canReadBlockedDrivers } from '../utils/access';

/**
 * Хук для загрузки перевозок (in_route), истории, статистики и заблокированных водителей.
 *
 * @param {Object} opts
 * @param {boolean}  opts.isGuard
 * @param {Object}   opts.isTopBaseOperator
 * @param {Object}   opts.isChppOperator
 * @param {Object}   opts.isPrivatePointOperator
 * @param {Object}   opts.user
 * @param {boolean}  opts.refresh    — триггер обновления (toggle)
 * @param {Function} opts.onNotify
 */
const useLoads = ({
  enabled = true,
  isGuard,
  isTopBaseOperator,
  isChppOperator,
  isPrivatePointOperator,
  user,
  refresh,
  onNotify,
  showInLoading,
  showInRoute,
  showHistory,
  showAnalytics,
}) => {
  const isAnyOperator = !!(isTopBaseOperator || isChppOperator || isPrivatePointOperator);
  const canReadGlobalLoads = canReadLoadQueues(user);
  const canLoadQueues = enabled && !!(isGuard || isAnyOperator || canReadGlobalLoads);
  const canLoadBlocked = enabled && !!(isGuard || isAnyOperator || canReadBlockedDrivers(user));
  const [pollRevision, setPollRevision] = useState(0);
  const sequence = useRef({ loading: 0, route: 0, completed: 0, stats: 0, blocked: 0 });
  const loadedQueries = useRef({ loading: null, route: null, completed: null });
  const analyticsLoaded = useRef({ stats: false, blocked: false });
  const [queueLoading, setQueueLoading] = useState(false);
  const [queuePending, setQueuePending] = useState(false);
  const [queueError, setQueueError] = useState(null);
  // One timer refreshes badge counts and the visible queue together.
  const [tabCounts, setTabCounts] = useState({ entered: 0, queued: 0, repair: 0, in_loading: 0, in_route: 0, completed: 0, drivers: 0 });

  useEffect(() => {
    if (!canLoadQueues) return;
    const id = setInterval(() => setPollRevision(value => value + 1), 5000);
    return () => clearInterval(id);
  }, [canLoadQueues]);

  useEffect(() => {
    if (!canLoadQueues) return;
    let active = true;
    const fetchCounts = async () => {
      try {
        const res = await getTabCounts();
        if (active && res?.data) setTabCounts(res.data);
      } catch (_) {/* silent */}
    };
    fetchCounts();
    return () => { active = false; };
  }, [canLoadQueues, refresh, pollRevision]);

  // Last 3 marks
  const [last3Marks, setLast3Marks] = useState([]);
  const [last3Loading, setLast3Loading] = useState(false);

  // In-loading loads
  const [inLoadingLoads, setInLoadingLoads] = useState([]);
  const [inLoadingCount, setInLoadingCount] = useState(0);
  const [inLoadingNext, setInLoadingNext] = useState(null);
  const [inLoadingPrev, setInLoadingPrev] = useState(null);
  const [inLoadingPage, setInLoadingPage] = useState(1);
  const [inLoadingDataPage, setInLoadingDataPage] = useState(1);
  const [inLoadingSearch, setInLoadingSearch] = useState('');

  // In-route loads
  const [inRouteLoads, setInRouteLoads] = useState([]);
  const [inRouteCount, setInRouteCount] = useState(0);
  const [inRouteNext, setInRouteNext] = useState(null);
  const [inRoutePrev, setInRoutePrev] = useState(null);
  const [inRoutePage, setInRoutePage] = useState(1);
  const [inRouteDataPage, setInRouteDataPage] = useState(1);
  const [inRouteSearch, setInRouteSearch] = useState('');

  // Completed loads
  const [completedLoads, setCompletedLoads] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [completedNext, setCompletedNext] = useState(null);
  const [completedPrev, setCompletedPrev] = useState(null);
  const [completedPage, setCompletedPage] = useState(1);
  const [completedDataPage, setCompletedDataPage] = useState(1);
  const [completedSearch, setCompletedSearch] = useState('');

  // History
  const [arrivedBLoads, setArrivedBLoads] = useState([]);
  const [arrivedBCount, setArrivedBCount] = useState(0);
  const [arrivedBNext, setArrivedBNext] = useState(null);
  const [arrivedBPrev, setArrivedBPrev] = useState(null);
  const [arrivedBPage, setArrivedBPage] = useState(1);
  const [arrivedBSearch, setArrivedBSearch] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTableLoading, setHistoryTableLoading] = useState(false);

  // Statistics
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);

  // Blocked drivers
  const [blockedDrivers, setBlockedDrivers] = useState([]);
  const [blockedDriversLoading, setBlockedDriversLoading] = useState(false);
  const [blockedDriversError, setBlockedDriversError] = useState(null);
  const [blockedDriversLoaded, setBlockedDriversLoaded] = useState(false);
  const [blockedDriversPage, setBlockedDriversPage] = useState(1);
  const [blockedDriversDataPage, setBlockedDriversDataPage] = useState(1);
  const [blockedDriversCount, setBlockedDriversCount] = useState(0);
  const BLOCKED_PAGE_SIZE = 10;

  // Time
  const [nowTime, setNowTime] = useState(moment());

  const changeInLoadingPage = useCallback(page => {
    const nextPage = typeof page === 'function' ? page(inLoadingDataPage) : page;
    if (nextPage === inLoadingPage) setPollRevision(value => value + 1);
    else setInLoadingPage(nextPage);
  }, [inLoadingDataPage, inLoadingPage]);
  const changeInRoutePage = useCallback(page => {
    const nextPage = typeof page === 'function' ? page(inRouteDataPage) : page;
    if (nextPage === inRoutePage) setPollRevision(value => value + 1);
    else setInRoutePage(nextPage);
  }, [inRouteDataPage, inRoutePage]);
  const changeCompletedPage = useCallback(page => {
    const nextPage = typeof page === 'function' ? page(completedDataPage) : page;
    if (nextPage === completedPage) setPollRevision(value => value + 1);
    else setCompletedPage(nextPage);
  }, [completedDataPage, completedPage]);

  // A pending or failed queue must not leak its state into another section.
  useEffect(() => {
    setQueueLoading(false);
    setQueuePending(false);
    setQueueError(null);
  }, [canLoadQueues, showInLoading, showInRoute, showHistory, showAnalytics]);

  // Сброс страницы при изменении поиска
  useEffect(() => { setInLoadingPage(1); }, [inLoadingSearch]);
  useEffect(() => { setInRoutePage(1); }, [inRouteSearch]);
  useEffect(() => { setCompletedPage(1); }, [completedSearch]);
  useEffect(() => { setArrivedBPage(1); }, [arrivedBSearch]);

  // Текущее время (обновляется каждую секунду)
  useEffect(() => {
    const interval = setInterval(() => setNowTime(moment()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Загрузка последних 3 отметок (В погрузке)
  useEffect(() => {
    const fetchLast3 = async () => {
      setLast3Loading(true);
      try {
        const res = await getLoadsInLoading({ page: 1, page_size: 3 });
        const data = res?.data?.results || [];
        setLast3Marks(data);
      } catch (e) {
        console.error('Ошибка загрузки последних отметок:', e);
      } finally {
        setLast3Loading(false);
      }
    };
    if (canLoadQueues && (canReadGlobalLoads || isGuard)) {
      fetchLast3();
    }
  }, [canLoadQueues, canReadGlobalLoads, isGuard, refresh, pollRevision]);

  // Загрузка загрузок "В погрузке"
  useEffect(() => {
    if (!canLoadQueues || !showInLoading) return;
    const sequenceState = sequence.current;

    const fetchInLoading = async () => {
      const token = ++sequence.current.loading;
      const query = JSON.stringify([inLoadingPage, inLoadingSearch]);
      setQueueLoading(loadedQueries.current.loading === null);
      setQueuePending(loadedQueries.current.loading !== query);
      try {
        const params = { page: inLoadingPage, page_size: 10 };
        if (inLoadingSearch) params.search = inLoadingSearch;
        
        let data = [];
        let count = 0;
        let next = null;
        let prev = null;

        const res = await getLoadsInLoading(params);
        data = res.data.results || [];
        count = res.data.count || 0;
        next = res.data.next;
        prev = res.data.previous;

        if (token !== sequence.current.loading) return;
        loadedQueries.current.loading = query;
        setQueueError(null);
        setInLoadingLoads(data);
        setInLoadingCount(count);
        setInLoadingNext(next);
        setInLoadingPrev(prev);
        setInLoadingDataPage(inLoadingPage);

      } catch (e) {
        if (token !== sequence.current.loading) return;
        setQueueError(tr("Не удалось загрузить очередь погрузки."));
      } finally {
        if (token === sequence.current.loading) {
          setQueueLoading(false);
          setQueuePending(false);
        }
      }
    };
    
    fetchInLoading();
    return () => { sequenceState.loading += 1; };
  }, [canLoadQueues, inLoadingPage, inLoadingSearch, refresh, pollRevision, showInLoading]);

  // Загрузка завершенных загрузок
  useEffect(() => {
    if (!canLoadQueues || !showHistory) return;
    const sequenceState = sequence.current;

    const fetchCompleted = async () => {
      const token = ++sequence.current.completed;
      const query = JSON.stringify([completedPage, completedSearch]);
      setQueueLoading(loadedQueries.current.completed === null);
      setQueuePending(loadedQueries.current.completed !== query);
      try {
        const params = { page: completedPage, page_size: 10 };
        if (completedSearch) params.search = completedSearch;

        let data = [];
        let count = 0;
        let next = null;
        let prev = null;

        const res = await getLoadsCompleted(params);
        data = res.data.results || [];
        count = res.data.count || 0;
        next = res.data.next;
        prev = res.data.previous;

        if (token !== sequence.current.completed) return;
        loadedQueries.current.completed = query;
        setQueueError(null);
        setCompletedLoads(data);
        setCompletedCount(count);
        setCompletedNext(next);
        setCompletedPrev(prev);
        setCompletedDataPage(completedPage);

      } catch (e) {
        if (token !== sequence.current.completed) return;
        setQueueError(tr("Не удалось загрузить историю перевозок."));
      } finally {
        if (token === sequence.current.completed) {
          setQueueLoading(false);
          setQueuePending(false);
        }
      }
    };

    fetchCompleted();
    return () => { sequenceState.completed += 1; };
  }, [canLoadQueues, completedPage, completedSearch, refresh, pollRevision, showHistory]);

  // Загрузка заблокированных водителей
  const fetchBlockedDrivers = useCallback(async (page = 1) => {
    const token = ++sequence.current.blocked;
    if (!canLoadBlocked) {
      analyticsLoaded.current.blocked = false;
      setBlockedDrivers([]);
      setBlockedDriversCount(0);
      setBlockedDriversLoaded(false);
      setBlockedDriversLoading(false);
      setBlockedDriversError(null);
      return;
    }
    setBlockedDriversLoading(!analyticsLoaded.current.blocked);
    try {
      const response = await getBlockedDrivers({ page, page_size: BLOCKED_PAGE_SIZE });
      if (token !== sequence.current.blocked) return;
      analyticsLoaded.current.blocked = true;
      setBlockedDrivers(response.data.results || []);
      setBlockedDriversCount(response.data.count || 0);
      setBlockedDriversDataPage(page);
      setBlockedDriversLoaded(true);
      setBlockedDriversError(null);
    } catch (error) {
      if (token === sequence.current.blocked) setBlockedDriversError(tr("Ошибка загрузки заблокированных водителей"));
    } finally {
      if (token === sequence.current.blocked) setBlockedDriversLoading(false);
    }
  }, [canLoadBlocked]);


  // Загрузка in-route перевозок
  useEffect(() => {
    if (!canLoadQueues || !showInRoute) return;
    const sequenceState = sequence.current;

    const fetchLoads = async () => {
      const token = ++sequence.current.route;
      const query = JSON.stringify([inRoutePage, inRouteSearch]);
      setQueueLoading(loadedQueries.current.route === null);
      setQueuePending(loadedQueries.current.route !== query);
      try {
          const params = { page: inRoutePage, page_size: 10 };
          if (inRouteSearch) params.search = inRouteSearch;
          const res = await getLoadByStatus(params);
          if (token !== sequence.current.route) return;
          loadedQueries.current.route = query;
          setQueueError(null);
          const inRouteData = res.data.results?.in_route || res.data.results?.active || [];
          const totalInRoute = res.data.results?.in_route_count ?? res.data.results?.active_count ?? 0;
          setInRouteLoads(inRouteData);
          setInRouteCount(totalInRoute);
          setInRouteNext(inRoutePage * 10 < totalInRoute ? 'next' : null);
          setInRoutePrev(inRoutePage > 1 ? 'prev' : null);
          setInRouteDataPage(inRoutePage);
      } catch (e) {
        if (token !== sequence.current.route) return;
        setQueueError(tr("Не удалось загрузить перевозки в пути."));
      } finally {
        if (token === sequence.current.route) {
          setQueueLoading(false);
          setQueuePending(false);
        }
      }
    };
    
    fetchLoads();

    return () => { sequenceState.route += 1; };
  }, [canLoadQueues, refresh, pollRevision, inRoutePage, inRouteSearch, showInRoute]);

  // Загрузка заблокированных водителей (отдельно для аналитики)
  useEffect(() => {
    if (!canLoadQueues || !showAnalytics) return;
    const sequenceState = sequence.current;
    fetchBlockedDrivers(blockedDriversPage);
    return () => { sequenceState.blocked += 1; };
  }, [canLoadQueues, showAnalytics, fetchBlockedDrivers, refresh, pollRevision, blockedDriversPage]);


  // Загрузка истории по клику
  const loadHistory = useCallback(async (isInitialLoad = false) => {
    if (!canLoadQueues) return;
    if (isInitialLoad) {
      setHistoryLoading(true);
      setShowHistoryModal(true);
    } else {
      setHistoryTableLoading(true);
    }
    try {
        const params = { page: arrivedBPage, page_size: 10 };
        if (arrivedBSearch) params.search = arrivedBSearch;
        const res = await getLoadHistory(params);
        const arrivedData = Array.isArray(res.data.results?.arrived_b) ? res.data.results.arrived_b : [];
        const totalArrived = res.data.results?.arrived_b_count || 0;
        setArrivedBLoads(arrivedData);
        setArrivedBCount(totalArrived);
        setArrivedBNext(arrivedBPage * 10 < totalArrived ? 'next' : null);
        setArrivedBPrev(arrivedBPage > 1 ? 'prev' : null);
    } catch (e) {
      onNotify?.({ isOpen: true, message: tr("Ошибка загрузки истории"), type: 'error' });
    } finally {
      if (isInitialLoad) setHistoryLoading(false);
      else setHistoryTableLoading(false);
    }
  }, [canLoadQueues, arrivedBPage, arrivedBSearch, onNotify]);

  // Перезагрузка при изменении страницы истории
  useEffect(() => {
    if (showHistoryModal) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivedBPage]);

  // Debounce для поиска по истории
  useEffect(() => {
    if (!showHistoryModal) return;
    const timer = setTimeout(() => {
      if (arrivedBPage === 1) loadHistory();
      else setArrivedBPage(1);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivedBSearch]);

  // Загрузка статистики
  useEffect(() => {
    if (!canLoadQueues || !showAnalytics) return;
    const sequenceState = sequence.current;

    const fetchStatistics = async () => {
      const token = ++sequence.current.stats;
      setStatsLoading(!analyticsLoaded.current.stats);
      try {
        const res = await getLoadStatistics();
        if (token !== sequence.current.stats) return;
        if (res?.data) { analyticsLoaded.current.stats = true; setStatsData(res.data); }
      } catch (e) {
        if (token === sequence.current.stats) console.error('Ошибка загрузки статистики:', e);
      } finally {
        if (token === sequence.current.stats) setStatsLoading(false);
      }
    };
    fetchStatistics();
    return () => { sequenceState.stats += 1; };
  }, [canLoadQueues, refresh, pollRevision, showAnalytics]);

  return {
    canReadLoads: canLoadQueues,
    canReadBlocked: canLoadBlocked,
    queueLoading, queuePending, queueError,
    // Last 3 marks
    last3Marks, last3Loading,
    // In-loading
    inLoadingLoads, inLoadingCount, inLoadingNext, inLoadingPrev,
    inLoadingPage, inLoadingDataPage, setInLoadingPage: changeInLoadingPage,
    inLoadingSearch, setInLoadingSearch,
    // In-route
    inRouteLoads, inRouteCount, inRouteNext, inRoutePrev,
    inRoutePage, inRouteDataPage, setInRoutePage: changeInRoutePage,
    inRouteSearch, setInRouteSearch,
    // Completed
    completedLoads, completedCount, completedNext, completedPrev,
    completedPage, completedDataPage, setCompletedPage: changeCompletedPage,
    completedSearch, setCompletedSearch,
    // History
    arrivedBLoads, arrivedBCount, arrivedBNext, arrivedBPrev,
    arrivedBPage, setArrivedBPage,
    arrivedBSearch, setArrivedBSearch,
    showHistoryModal, setShowHistoryModal,
    historyLoading, historyTableLoading,
    loadHistory,
    // Stats
    statsLoading, statsData,
    // Blocked
    blockedDrivers, blockedDriversLoading, blockedDriversError,
    blockedDriversPage, blockedDriversDataPage, setBlockedDriversPage, blockedDriversCount, blockedDriversLoaded, BLOCKED_PAGE_SIZE,
    // Time
    nowTime,
    // Tab counts share the visible queue's refresh trigger.
    tabCounts,
  };
};

export default useLoads;
